import { useEffect, useState } from 'preact/hooks';
import { authFetch } from '../api/authFetch';
import { useModels } from '../hooks/useModels';
import { modelBadge } from '../utils/modelHelpers';
import { vaNextUrl } from '../utils/estateLinks';
// P2.4 lands the interactive picker. The read-only summary still reuses `modelDescription` (helper
// altitude); tapping a row now opens the full `<ModelPickerList>` (the edit affordance the P2.1
// extraction + the standalone-styling finding unblocked) to rebind the cascade model.
import { ModelPickerList, modelDescription } from '../shared-ui/ModelPickerList';
import { isEmbeddingModel } from '@voice-assistant/shared';

/**
 * Harness Studio — `/harness` route (Phase 2.3 read-only shell → Phase 2.4 cascade DATA editor).
 *
 * Mirrors `/arch` (a path-routed standalone full page dispatched from App.tsx before the chat shell).
 * Lists the cascade model bindings from `GET /harness/config?scope=user`. P2.4 makes them EDITABLE:
 * tap a row → `<ModelPickerList>` → `POST /harness/config { key, value }` rebinds the admin's OWN
 * `user`-scope override; "Reset to default" → `DELETE /harness/config?key=` removes the row (→ seed).
 *
 * GATE STATE IS DERIVED SOLELY FROM THE FETCH STATUS. The backend (`harness.controller.ts`) is the
 * single authority — its 404-before-403 order means a flag-off deployment reads as *absent* (404)
 * for any caller, and a non-admin reads as *forbidden* (403). We deliberately do NOT also branch on
 * `useRoles()`: two async truth sources that can momentarily disagree are exactly the unsynced-state
 * flicker this codebase keeps writing post-mortems about. One authority — the response status.
 *
 * SERVER-AUTHORITATIVE WRITES. After a POST/DELETE we update the row from the SERVER's post-write
 * resolution (the response's `{ value, layer }`) — we never synthesize `layer:'user'` on the client.
 * A malformed response triggers a full refetch instead of a client guess. (Local state + refetch, not
 * a Zustand FSM: the page is single, server-authoritative, and refetch IS the reconciliation — no
 * cross-store sync or persist-across-reload to coordinate.)
 */

type HarnessLayer = 'user' | 'profile' | 'global' | 'seed';

// Mirrors `HarnessBindingView` in backend/src/harness/harness.controller.ts.
interface HarnessBindingView {
  key: string;
  label: string;
  value: string;
  layer: HarnessLayer;
  seedDefault: string;
  dangerTier: 'DATA' | 'CODE';
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'ok'; scope: string; bindings: HarnessBindingView[] }
  | { kind: 'forbidden' } // 403 — authenticated but not an admin
  | { kind: 'unavailable' } // 404 — feature flag off (or the endpoint isn't deployed yet)
  | { kind: 'error'; message: string };

const LAYER_COLOR: Record<HarnessLayer, string> = {
  user: '#7aa2f7',
  profile: '#bb9af7',
  global: '#e0af68',
  seed: '#9ece6a',
};

function LayerPill({ layer }: { layer: HarnessLayer }) {
  const c = LAYER_COLOR[layer] ?? '#9aa5ce';
  return (
    <span
      title={layer === 'seed' ? 'resolved from the seed default (no override)' : `overridden at the ${layer} layer`}
      style={`display:inline-flex;align-items:center;gap:4px;font-size:11px;padding:2px 8px;border-radius:999px;border:1px solid ${c};color:${c};background:${c}1a;`}
    >
      resolved via {layer}
    </span>
  );
}

interface BindingRowProps {
  row: HarnessBindingView;
  modelName: string;
  description: string;
  models: ReturnType<typeof useModels>['models'];
  open: boolean;
  busy: boolean;
  error: string;
  onToggle: () => void;
  onPick: (modelId: string) => void;
  onReset: () => void;
}

function BindingRow({ row, modelName, description, models, open, busy, error, onToggle, onPick, onReset }: BindingRowProps) {
  const badge = modelBadge(row.value, false);
  const overridden = row.layer === 'user'; // v1 writes only the user layer; only it is resettable here
  return (
    <li
      style="list-style:none;display:flex;flex-direction:column;gap:8px;padding:12px 14px;border:1px solid var(--border,#2a2a3a);border-radius:10px;background:var(--card,#16161e);"
    >
      <div style="display:flex;gap:12px;align-items:flex-start;">
        <div
          style={`flex-shrink:0;width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;background:${badge.color}2e;border:1px solid ${badge.color}59;color:${badge.color};`}
        >
          {badge.label}
        </div>
        <div style="flex:1;min-width:0;">
          <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;">
            <span style="font-weight:600;font-size:14px;">{row.label}</span>
            <span
              style={`font-size:10px;padding:1px 6px;border-radius:4px;border:1px solid ${row.dangerTier === 'CODE' ? '#f7768e' : '#565f89'};color:${row.dangerTier === 'CODE' ? '#f7768e' : '#9aa5ce'};`}
              title={row.dangerTier === 'CODE' ? 'CODE binding — view-only, operator/isolation-gated' : 'DATA binding — config-only, editable by an admin'}
            >
              {row.dangerTier}
            </span>
          </div>
          <code style="font-size:11px;opacity:0.55;word-break:break-all;">{row.key}</code>
          <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-top:6px;">
            <span style="font-weight:600;font-size:13px;">{modelName}</span>
            <LayerPill layer={row.layer} />
          </div>
          {description && <div style="font-size:12px;opacity:0.7;margin-top:2px;">{description}</div>}
          {overridden && (
            <div style="font-size:11px;opacity:0.6;margin-top:4px;">seed default: {row.seedDefault}</div>
          )}
        </div>
        <div style="display:flex;flex-direction:column;gap:6px;align-items:flex-end;flex-shrink:0;">
          <button
            type="button"
            disabled={busy}
            onClick={onToggle}
            style={`background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:3px 10px;font-size:12px;cursor:${busy ? 'default' : 'pointer'};opacity:${busy ? 0.5 : 1};min-height:30px;white-space:nowrap;`}
          >
            {open ? 'Close' : 'Edit ▾'}
          </button>
          {overridden && (
            <button
              type="button"
              disabled={busy}
              onClick={onReset}
              title="Delete this override row → fall back to the seed default (and track future default changes)"
              style={`background:none;border:1px solid #565f89;color:#9aa5ce;border-radius:6px;padding:3px 10px;font-size:12px;cursor:${busy ? 'default' : 'pointer'};opacity:${busy ? 0.5 : 1};min-height:30px;white-space:nowrap;`}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {busy && <div style="font-size:12px;opacity:0.7;">Saving…</div>}
      {error && <div style="font-size:12px;color:var(--danger,#f7768e);">{error}</div>}

      {open && !busy && (
        <div style="border-top:1px solid var(--border,#2a2a3a);padding-top:8px;">
          <ModelPickerList
            models={models}
            selectedModelId={row.value}
            onPick={onPick}
          />
        </div>
      )}
    </li>
  );
}

export function HarnessPage() {
  const { models } = useModels();
  // Role-sensibility: only offer models that can serve as a cascade AGENT. Embedding-only models
  // (vectors, not text) would silently break the cascade if bound — drop them from the PICKER. Keep
  // the FULL `models` for the display `.find` below so a pre-existing/seed binding still resolves its
  // name. Same shared `isEmbeddingModel` the backend write-guard enforces, so the two can't drift. (#1751)
  const pickerModels = models.filter((m) => !isEmbeddingModel(m.modelId));
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [writing, setWriting] = useState<Record<string, boolean>>({});
  const [writeErr, setWriteErr] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    authFetch('/api/harness/config?scope=user')
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 403) {
          setState({ kind: 'forbidden' });
          return;
        }
        if (r.status === 404) {
          setState({ kind: 'unavailable' });
          return;
        }
        if (!r.ok) {
          setState({ kind: 'error', message: `HTTP ${r.status}` });
          return;
        }
        const data = await r.json().catch(() => null);
        if (cancelled) return;
        setState({
          kind: 'ok',
          scope: data?.scope ?? 'user',
          // Null-safe: a malformed 200 must render an empty list, never null-deref the page blank.
          bindings: Array.isArray(data?.bindings) ? data.bindings : [],
        });
      })
      .catch((e) => {
        if (!cancelled) setState({ kind: 'error', message: String(e?.message || e) });
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  // Server-authoritative in-place row update from a write response's { value, layer }. NEVER
  // synthesizes a layer — a malformed response falls back to a full refetch.
  function applyServerResolution(key: string, data: unknown) {
    const d = data as { value?: unknown; layer?: unknown } | null;
    if (d && typeof d.value === 'string' && typeof d.layer === 'string') {
      setState((s) =>
        s.kind === 'ok'
          ? { ...s, bindings: s.bindings.map((b) => (b.key === key ? { ...b, value: d.value as string, layer: d.layer as HarnessLayer } : b)) }
          : s,
      );
    } else {
      setNonce((n) => n + 1); // re-derive from the server rather than guess
    }
  }

  async function runWrite(key: string, req: () => Promise<Response>) {
    setWriting((w) => ({ ...w, [key]: true }));
    setWriteErr((e) => ({ ...e, [key]: '' }));
    try {
      const r = await req();
      if (!r.ok) {
        const msg = await r
          .json()
          .then((d) => (d && typeof d.message === 'string' ? d.message : null))
          .catch(() => null);
        setWriteErr((e) => ({ ...e, [key]: msg || `HTTP ${r.status}` }));
        return;
      }
      const data = await r.json().catch(() => null);
      applyServerResolution(key, data);
      setOpenKey(null);
    } catch (err) {
      setWriteErr((e) => ({ ...e, [key]: String((err as { message?: string })?.message || err) }));
    } finally {
      setWriting((w) => ({ ...w, [key]: false }));
    }
  }

  const handlePick = (key: string, modelId: string) =>
    runWrite(key, () =>
      authFetch('/api/harness/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: modelId }),
      }),
    );

  const handleReset = (key: string) =>
    runWrite(key, () => authFetch(`/api/harness/config?key=${encodeURIComponent(key)}`, { method: 'DELETE' }));

  return (
    <div class="harness-page" style="display:flex;flex-direction:column;height:100vh;overflow:hidden;">
      <header style="padding:10px 14px;border-bottom:1px solid var(--border,#2a2a3a);flex-shrink:0;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:4px;">
          <span style="font-weight:600;font-size:15px;">⚙ Harness Studio</span>
          <div style="display:flex;gap:6px;align-items:center;flex-shrink:0;">
            <span
              style="font-size:11px;padding:2px 8px;border-radius:999px;border:1px solid #565f89;color:#9aa5ce;"
              title="Admin-only. Edits write your OWN user-scope override; other users are unaffected."
            >
              admin · user scope
            </span>
            {/* The estate deep-link triangle (issue #2208): this estate-ops surface links OUT to
                va-next mission control instead of re-rendering estate state. The href is the
                convention's builder (utils/estateLinks.ts) — never a hand-rolled literal; no
                ?seat=/?lane= param is fabricated because this page carries no seat/lane mapping.
                Muted chrome idiom (not the accent) — an external secondary navigation. */}
            <a
              href={vaNextUrl()}
              data-link-kind="va-next-estate"
              target="_blank"
              rel="noreferrer"
              title="Open the estate in va-next — mission control (lanes, seats, council) in a new tab"
              style="background:none;border:1px solid #565f89;color:#9aa5ce;border-radius:6px;padding:4px 10px;font-size:12px;text-decoration:none;min-height:32px;display:inline-flex;align-items:center;"
            >
              Estate ↗
            </a>
            <a
              href="/"
              style="background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:4px 10px;font-size:12px;text-decoration:none;min-height:32px;display:inline-flex;align-items:center;"
            >
              ← App
            </a>
          </div>
        </div>
        <div style="font-size:12px;opacity:0.65;">
          The role→model bindings that govern the cascade agent. Tap Edit to rebind a model for your own
          sessions; Reset removes the override and tracks the seed default.
        </div>
      </header>

      <main style="flex:1;overflow:auto;padding:14px;">
        {state.kind === 'loading' && <div style="opacity:0.7;">Loading harness configuration…</div>}

        {state.kind === 'unavailable' && (
          <div style="opacity:0.8;line-height:1.5;">
            <div style="font-weight:600;margin-bottom:6px;">Harness Studio is not available on this deployment.</div>
            <div style="font-size:13px;opacity:0.8;">
              It's gated behind the <code>harness_studio_enabled</code> profile flag (prod + self-hosted).
              If you just deployed, the backend endpoint may not be live yet.
            </div>
            <a href="/" style="color:var(--accent,#7aa2f7);display:inline-block;margin-top:10px;">← Back to app</a>
          </div>
        )}

        {state.kind === 'forbidden' && (
          <div style="opacity:0.8;line-height:1.5;">
            <div style="font-weight:600;margin-bottom:6px;">Not authorized.</div>
            <div style="font-size:13px;opacity:0.8;">
              Harness Studio is admin-only. You can still inspect how each turn resolved its model in the
              per-turn details drawer (tap a message's model badge).
            </div>
            <a href="/" style="color:var(--accent,#7aa2f7);display:inline-block;margin-top:10px;">← Back to app</a>
          </div>
        )}

        {state.kind === 'error' && (
          <div style="color:var(--danger,#f7768e);">
            Failed to load harness configuration: {state.message}
            <button
              type="button"
              onClick={() => setNonce((n) => n + 1)}
              style="margin-left:8px;background:none;border:1px solid var(--danger,#f7768e);color:var(--danger,#f7768e);border-radius:6px;padding:2px 10px;cursor:pointer;"
            >
              Retry
            </button>
          </div>
        )}

        {state.kind === 'ok' && (
          <>
            <div style="font-size:12px;opacity:0.6;margin-bottom:10px;">
              Cascade model bindings · scope: {state.scope} · {state.bindings.length} binding
              {state.bindings.length === 1 ? '' : 's'}
            </div>
            {state.bindings.length === 0 ? (
              <div style="opacity:0.7;">No cascade bindings returned.</div>
            ) : (
              <ul style="display:flex;flex-direction:column;gap:8px;padding:0;margin:0;">
                {state.bindings.map((row) => {
                  const m = models.find((mm) => mm.modelId === row.value);
                  return (
                    <BindingRow
                      key={row.key}
                      row={row}
                      modelName={m?.displayName ?? row.value}
                      description={m ? modelDescription(m) : ''}
                      models={pickerModels}
                      open={openKey === row.key}
                      busy={!!writing[row.key]}
                      error={writeErr[row.key] ?? ''}
                      onToggle={() => setOpenKey((k) => (k === row.key ? null : row.key))}
                      onPick={(modelId) => handlePick(row.key, modelId)}
                      onReset={() => handleReset(row.key)}
                    />
                  );
                })}
              </ul>
            )}
            <div style="font-size:11px;opacity:0.5;margin-top:14px;line-height:1.5;">
              Edits are scoped to your own <code>user</code> row — they supersede the seed for your sessions
              only and don't affect other users. Reset deletes the override (derive-first: a later default
              change is then inherited).
            </div>
          </>
        )}
      </main>
    </div>
  );
}
